import { FC, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LinkSimple, Sparkle, X } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import {
  ICoachSessionAthlete,
  coachSessionsEndpoints,
} from '../../../endpoints/coachSessions';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import WorkoutBuilderModal from '../../../components/ProgrammingPage/WorkoutBuilderModal/WorkoutBuilderModal';
import { IWorkout } from '../../../models/programming';
import styles from './PhysicalBlock.module.scss';
import refStyles from './PhysicalRefsList.module.scss';

type Mode = 'link' | 'inline';

interface AddPhysicalRefDialogProps {
  sessionId: string;
  blockOrder: number;
  rosterAthletes: ICoachSessionAthlete[];
  alreadyAssigned: Set<string>;
  onClose: () => void;
}

/**
 * Bug 69f87a4b — Dialog per aggiungere una scheda al blocco fisico (multi-card).
 * Richiede sempre la selezione di almeno un atleta del team. Atleti già
 * assegnati ad altre schede sono mostrati ma disabilitati.
 */
export const AddPhysicalRefDialog: FC<AddPhysicalRefDialogProps> = ({
  sessionId,
  blockOrder,
  rosterAthletes,
  alreadyAssigned,
  onClose,
}) => {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('inline');

  const { data: workoutsData } = useQuery({
    queryKey: ['programmingWorkouts'],
    queryFn: () =>
      endpoints.getProgrammingWorkouts({ limit: 100 }).then((r) => r.data),
    enabled: mode === 'link',
  });
  const workouts: any[] = Array.isArray(workoutsData)
    ? workoutsData
    : (workoutsData?.docs ?? []);

  const [selectedId, setSelectedId] = useState('');
  const [inlineTitle, setInlineTitle] = useState('');
  const [inlineDuration, setInlineDuration] = useState<number>(30);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // Bug 6a242e9b — crea scheda da zero col builder completo palestra, poi linkala al blocco
  const [showBuilder, setShowBuilder] = useState(false);

  const linkCreatedWorkout = (w: IWorkout) => {
    coachSessionsEndpoints
      .addPhysicalWorkRef(sessionId, blockOrder, {
        gymSessionId: (w as any)._id,
        assignedAthleteIds: Array.from(picked),
      })
      .then(() => {
        setShowBuilder(false);
        queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });
        onClose();
      });
  };

  const availableAthletes = useMemo(
    () => rosterAthletes.filter((a) => !alreadyAssigned.has(a.athleteId)),
    [rosterAthletes, alreadyAssigned],
  );

  // Bug 6a242e9b — anteprima esercizi della scheda selezionata (esercizi · serie×reps),
  // così il coach vede cosa contiene prima di confermarla nel blocco atletico.
  const selectedWorkoutPreview = useMemo<{ name: string; meta: string }[] | null>(() => {
    if (mode !== 'link' || !selectedId) return null;
    const w: any = workouts.find((x: any) => x._id === selectedId);
    if (!w) return null;
    const days: any[] = w.days ?? (w.blocks ? [{ blocks: w.blocks }] : []);
    const out: { name: string; meta: string }[] = [];
    for (const d of days) {
      for (const b of d.blocks ?? []) {
        for (const ex of b.exercises ?? []) {
          const name =
            ex.exercise?.name?.it ||
            ex.exercise?.name?.en ||
            ex.exerciseName ||
            'Esercizio';
          const load = ex.loads?.[0]?.value
            ? ` @ ${ex.loads[0].value}${ex.loads[0].unit ? ` ${ex.loads[0].unit}` : ''}`
            : '';
          const meta = ex.steps?.length
            ? `${ex.steps.length} step`
            : `${ex.sets ?? '—'}×${ex.reps ?? '—'}${load}`;
          out.push({ name, meta });
        }
      }
    }
    return out;
  }, [mode, selectedId, workouts]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });

  const addMut = useMutation({
    mutationFn: () => {
      const body =
        mode === 'link'
          ? {
              gymSessionId: selectedId,
              assignedAthleteIds: Array.from(picked),
            }
          : {
              title: inlineTitle.trim(),
              duration: inlineDuration,
              assignedAthleteIds: Array.from(picked),
            };
      return coachSessionsEndpoints
        .addPhysicalWorkRef(sessionId, blockOrder, body)
        .then((r) => r.data);
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const togglePick = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const valid =
    picked.size > 0 &&
    (mode === 'link' ? !!selectedId : !!inlineTitle.trim());

  return (
    <>
    <div className={styles.dialogBackdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <h3>Aggiungi scheda al blocco</h3>
          <button type="button" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.dialogModeTabs}>
          <button
            type="button"
            className={`${styles.modeTab} ${mode === 'inline' ? styles.modeTabActive : ''}`}
            onClick={() => setMode('inline')}
          >
            <Sparkle size={14} /> Crea al volo
          </button>
          <button
            type="button"
            className={`${styles.modeTab} ${mode === 'link' ? styles.modeTabActive : ''}`}
            onClick={() => setMode('link')}
          >
            <LinkSimple size={14} /> Collega esistente
          </button>
        </div>

        <div className={styles.dialogBody}>
          {mode === 'inline' && (
            <>
              <label className={styles.dialogLabel}>Nome scheda</label>
              <input
                className={styles.dialogInput}
                value={inlineTitle}
                onChange={(e) => setInlineTitle(e.target.value)}
                placeholder="Es. Forza massimale gruppo A"
              />
              <label className={styles.dialogLabel}>
                Durata stimata (min)
              </label>
              <input
                type="number"
                className={styles.dialogInput}
                value={inlineDuration}
                onChange={(e) => setInlineDuration(Number(e.target.value))}
                min={5}
                max={180}
              />
              {/* Bug 6a242e9b — crea scheda completa (esercizi · serie×reps) come in palestra */}
              <button
                type="button"
                className={refStyles.fullBuilderBtn}
                disabled={picked.size === 0}
                title={picked.size === 0 ? 'Seleziona prima gli atleti destinatari' : undefined}
                onClick={() => setShowBuilder(true)}
              >
                <Sparkle size={14} /> Crea scheda completa (esercizi · serie×reps)
              </button>
            </>
          )}
          {mode === 'link' && (
            <>
              <label className={styles.dialogLabel}>
                Seleziona scheda dalla library
              </label>
              <select
                className={styles.dialogSelect}
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">— Seleziona —</option>
                {workouts.map((w: any) => (
                  <option key={w._id} value={w._id}>
                    {w.name ?? w.title ?? w._id} ·{' '}
                    {w.totalDurationMinutes ?? w.durationMinutes ?? '—'}'
                  </option>
                ))}
              </select>

              {/* Bug 6a242e9b — preview del contenuto della scheda prima di confermare */}
              {selectedWorkoutPreview && (
                <div className={refStyles.schedaPreview}>
                  {selectedWorkoutPreview.length === 0 ? (
                    <span className={refStyles.muted}>Nessun esercizio in questa scheda.</span>
                  ) : (
                    selectedWorkoutPreview.map((ex, i) => (
                      <div key={i} className={refStyles.schedaPreviewRow}>
                        <span className={refStyles.schedaPreviewName}>{ex.name}</span>
                        <span className={refStyles.schedaPreviewMeta}>{ex.meta}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          <label className={styles.dialogLabel} style={{ marginTop: 12 }}>
            Atleti destinatari ({picked.size}/{availableAthletes.length}{' '}
            disponibili)
          </label>
          <div className={refStyles.athletePicker}>
            {availableAthletes.length === 0 && (
              <span className={refStyles.muted}>
                Tutti gli atleti del team sono già assegnati ad altre schede di
                questo blocco.
              </span>
            )}
            {availableAthletes.map((a) => (
              <label key={a.athleteId} className={refStyles.athleteRow}>
                <input
                  type="checkbox"
                  checked={picked.has(a.athleteId)}
                  onChange={() => togglePick(a.athleteId)}
                />
                <span>
                  {`${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() ||
                    a.athleteId.slice(-6)}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.dialogFooter}>
          <CustomButton color="outline" size="sm" onClick={onClose}>
            Annulla
          </CustomButton>
          <CustomButton
            color="primary"
            size="sm"
            disabled={!valid || addMut.isPending}
            isLoading={addMut.isPending}
            onClick={() => addMut.mutate()}
          >
            {mode === 'inline' ? 'Crea e aggiungi' : 'Aggiungi'}
          </CustomButton>
        </div>
      </div>
    </div>
    {showBuilder && (
      <WorkoutBuilderModal
        isOpen={showBuilder}
        workout={null}
        onClose={() => setShowBuilder(false)}
        onSaved={(w) => linkCreatedWorkout(w)}
      />
    )}
    </>
  );
};
